import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert, CircularProgress
} from '@mui/material';
import { parseOrderPdf } from '../../services/retailOrdersService';

/**
 * Shared "upload an order PDF" dialog. Calls the parse-only endpoint, previews
 * the extracted header + line items (low-confidence SKU rows flagged amber),
 * and on "Apply to form" hands the normalized prefill payload to the parent via
 * onApply(prefill). It never saves — the parent form owns the save.
 */
export default function OrderPdfUpload({ open, onClose, onApply, orderType, brandId, tierId, warehouseCode }) {
  const [loading, setLoading] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [error, setError] = useState('');

  const reset = () => { setPrefill(null); setError(''); setLoading(false); };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(''); setPrefill(null); setLoading(true);
    try {
      const data = await parseOrderPdf(file, { brandId, orderType, tierId, warehouseCode });
      setPrefill(data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to parse PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { reset(); onClose(); };

  const lineItems = prefill?.line_items || [];
  const unmatched = lineItems.filter((li) => !li.matched).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload order PDF</DialogTitle>
      <DialogContent dividers>
        {!prefill && (
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" component="label" disabled={loading}>
              {loading ? 'Parsing…' : 'Choose PDF'}
              <input type="file" accept="application/pdf" hidden onChange={handleFile} />
            </Button>
            {loading && <CircularProgress size={20} />}
            <Typography variant="caption" color="text.secondary">
              The order is extracted and SKUs matched. Nothing is saved until you review and submit the form.
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        {prefill && (prefill.warnings || []).map((w, i) => (
          <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
        ))}

        {prefill && lineItems.length > 0 && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {prefill.order?.customer_name || 'Unknown customer'}
              {' · PO '}{prefill.order?.po_number || '—'}
              {' · '}{lineItems.length} lines
              {unmatched > 0 && ` · ${unmatched} need a SKU`}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>From PDF</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((li, i) => (
                  <TableRow key={i} sx={{ bgcolor: li.matched ? 'inherit' : 'warning.light' }}>
                    <TableCell>{li.product_sku || <em>— pick —</em>}</TableCell>
                    <TableCell>{li.raw_text}</TableCell>
                    <TableCell align="right">{li.quantity}</TableCell>
                    <TableCell align="right">{li.unit_price}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={li.matched ? 'success' : 'warning'}
                        label={li.matched ? li.match_method : `${li.match_method} ${Math.round((li.match_confidence || 0) * 100)}%`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {prefill && lineItems.length === 0 && !error && (
          <Typography variant="body2">No line items were extracted from this PDF.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!prefill || lineItems.length === 0}
          onClick={() => { onApply(prefill); handleClose(); }}
        >
          Apply to form
        </Button>
      </DialogActions>
    </Dialog>
  );
}
